import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { setQuotationParams, cleanComparisonPlans } from '../../../../../actions/searchActions';

import Input from '../../../../../components/V2/input';
import { PinIcon } from '../../../../../assets/icons/Pin.icon';
import { CalendarIcon } from '../../../../../assets/icons/Calendar.incon';
import { formatURL, getDaysDiff, buildSearchWithDestinationCategories } from '../../../../../utils';
import dataLayer from '../../../../../utils/dataLayer';
import { useSearchBarCommon } from '../../hooks/useSearchBarCommon';
import { formatDateBrFromDate, parseDateBrToDate, toISO, parseSearchFromPath } from '../../utils/searchParsing';

import styles from './styles.module.css';
import Dropdown from '../../../../../components/V2/dropdown';
import MobileDatepicker from '../../../../../components/V2/datepicker/mobile-fullscreen';
import SearchIcon from '../../../../../components/search/searchbar/SearchIcon';
import { ChevronRightIcon } from '../../../../../assets/icons/ChevronRight.icon';
import { CloseIcon } from '../../../../../assets/icons/Close.icon';

const SearchBarMobile = (props) => {
  const [range, setRange] = useState({ startDate: null, endDate: null });
  const [destinationDropdownOpen, setDestinationDropdownOpen] = useState(false);
  const [isMobileDatepickerOpen, setIsMobileDatepickerOpen] = useState(false);
  const [datepickMode, setDatepickMode] = useState('range');
  const [isInputSectionOpen, setIsInputSectionOpen] = useState(false);

  const { containerRef, isScrolled, containerHeight, computedDestinationName, beginBr, endBr, permanence } =
    useSearchBarCommon(props.destinations);

  const [userDestinationSlug, setUserDestinationSlug] = useState(null);
  const [userBeginBr, setUserBeginBr] = useState(null);
  const [userEndBr, setUserEndBr] = useState(null);

  useEffect(() => {
    const body = document.body;
    const lock = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      body.dataset.scrollY = String(scrollY);
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    };
    const unlock = () => {
      const y = parseInt(body.dataset.scrollY || '0', 10) || 0;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      delete body.dataset.scrollY;
      window.scrollTo(0, y);
    };

    if (isInputSectionOpen) {
      lock();
    } else if (body.style.position === 'fixed') {
      unlock();
    }

    return () => {
      if (body.style.position === 'fixed') {
        unlock();
      }
    };
  }, [isInputSectionOpen]);

  const openDatepickerFor = useCallback(
    (mode) => {
      const beginBrValue = userBeginBr != null ? userBeginBr : beginBr || '';
      const endBrValue = userEndBr != null ? userEndBr : endBr || '';
      const start = parseDateBrToDate(beginBrValue);
      const end = parseDateBrToDate(endBrValue);
      setRange({ startDate: start, endDate: end });
      let effective = mode;
      if (!start && !end) {
        effective = 'range';
      } else if (start && end) {
        effective = 'range';
      } else if (mode !== 'start' && mode !== 'end') {
        effective = 'range';
      }
      setDatepickMode(effective);
      setIsMobileDatepickerOpen(true);
    },
    [userBeginBr, userEndBr, beginBr, endBr]
  );

  const searchPlan = useCallback(() => {
    const { destinationSlug: pathDestSlug, beginDate: pathBegin, endDate: pathEnd } = parseSearchFromPath();
    const beginISO = range.startDate ? toISO(range.startDate) : pathBegin || '';
    const endISO = range.endDate ? toISO(range.endDate) : pathEnd || '';

    const destination = userDestinationSlug || pathDestSlug;

    const isValidISO = (s) => /^(\d{4})-(\d{2})-(\d{2})$/.test(s || '');
    if (!isValidISO(beginISO) || !isValidISO(endISO) || !destination) {
      return;
    }

    if (props.cleanComparisonPlans) props.cleanComparisonPlans();

    const search = buildSearchWithDestinationCategories(props.destinations, destination);
    const path = formatURL(
      props.match && props.match.params ? props.match.params : {},
      `/pesquisa/${destination}/${beginISO}/${endISO}`,
      search,
      !search
    );
    if (props.history && props.history.push) props.history.push(path);

    const diffDays = getDaysDiff(new Date(beginISO), new Date(endISO));
    dataLayer.raw({
      event: 'envio_form_cotacao',
      page_category: 'home',
      tipo_envio: 'novo_lead',
      cotacao: {
        destino: destination,
        data_embarque: beginISO,
        data_desembarque: endISO,
        tempo_permanencia: !Number.isNaN(diffDays) ? diffDays : undefined,
      },
    });
    dataLayer.searchSubmitted(destination, beginISO, endISO);
  }, [userDestinationSlug, range.startDate, range.endDate]);

  const handleCloseAll = useCallback(() => {
    setIsMobileDatepickerOpen(false);
    setIsInputSectionOpen(false);
    setDestinationDropdownOpen(false);
  }, []);

  const displayedDestinationName = (() => {
    if (userDestinationSlug) {
      const found = (props.destinations || []).find((d) => d.slug === userDestinationSlug);
      return found ? found.name : '';
    }
    return computedDestinationName || '';
  })();

  return (
    <div>
      {isScrolled && <div style={{ height: containerHeight }} />}
      <div
        ref={containerRef}
        className={[styles.container, isScrolled ? styles.fixed : '', isScrolled ? styles.scrolled : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles.content}>
          <div className={styles.search}>
            <div className={styles.destination} onClick={() => setIsInputSectionOpen(true)}>
              <div className={styles.item}>
                <PinIcon />
                <strong>{displayedDestinationName}</strong>
              </div>
              <div className={styles.item}>
                <CalendarIcon />
                <span>
                  <strong>{`${userBeginBr != null ? userBeginBr : beginBr || ''} - ${
                    userEndBr != null ? userEndBr : endBr || ''
                  } `}</strong>
                  ({permanence})
                </span>
              </div>
            </div>

            <button className={styles.searchButton} type="button" onClick={() => setIsInputSectionOpen(true)}>
              <SearchIcon />
            </button>
          </div>
        </div>
      </div>

      {isInputSectionOpen && (
        <React.Fragment>
          <div
            className={styles.overlay}
            onClick={handleCloseAll}
          />
          <div className={styles.inputSection}>
            <div className={styles.inputSectionContent}>
              <div className={styles.header}>
                <strong>Editar pesquisa</strong>

                <div
                  className={styles.closeIcon}
                  onClick={handleCloseAll}
                >
                  <CloseIcon size={24} />
                </div>
              </div>

              <div className={styles.flexContainer}>
                <div className={styles.destinationInput}>
                  <Input
                    icon={<PinIcon size={24} />}
                    label="Destino:"
                    value={displayedDestinationName}
                    onClick={(e) => {
                      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                      setDestinationDropdownOpen((prev) => !prev);
                    }}
                    className={styles.input}
                    disabled={props.isLoading}
                    rightIcon={destinationDropdownOpen ? <ChevronRightIcon size={24} color="#007E47" rotate={270} />: <ChevronRightIcon size={24} color="#007E47" rotate={90} />}
                  />
                  <Dropdown isOpen={destinationDropdownOpen} onClose={() => setDestinationDropdownOpen(false)}>
                    {props.destinations && props.destinations.length > 0 && (
                      <ul className={styles.destinationList}>
                        {props.destinations.map((d) => (
                          <li
                            key={d.id}
                            className={styles.destinationItem}
                            onClick={() => {
                              setUserDestinationSlug(d.slug);
                              setDestinationDropdownOpen(false);
                            }}
                          >
                            {d.name}
                            {(d.slug === 'brasil' || d.slug === 'receptivo') && (
                              <span className={styles.description}>
                                {d.slug === 'brasil'
                                  ? 'Para viagens dentro do Brasil'
                                  : 'Para quem vem visitar o Brasil'}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Dropdown>
                </div>

                <div className={styles.inputGroup}>
                  <Input
                    icon={<CalendarIcon size={24} />}
                    label="Embarque no Brasil:"
                    value={userBeginBr != null ? userBeginBr : beginBr || ''}
                    variant="column"
                    onClick={() => openDatepickerFor('start')}
                  />
                  <Input
                    icon={<CalendarIcon size={24} />}
                    label="Desembarque no Brasil:"
                    value={userEndBr != null ? userEndBr : endBr || ''}
                    variant="column"
                    onClick={() => openDatepickerFor('end')}
                  />
                </div>

                <button className={styles.updateSearchButton} onClick={searchPlan}>
                  Atualizar pesquisa
                </button>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
      <MobileDatepicker
        open={isMobileDatepickerOpen}
        selectionMode={datepickMode}
        value={range}
        onChange={(val) => {
          setRange(val);
        }}
        onConfirm={(val) => {
          setRange(val);
          setUserBeginBr(formatDateBrFromDate(val.startDate));
          setUserEndBr(formatDateBrFromDate(val.endDate));
          setIsMobileDatepickerOpen(false);
        }}
        onBack={() => setIsMobileDatepickerOpen(false)}
        onClose={handleCloseAll}
        minDate={new Date()}
        allowSameDay
        labels={{ title: 'Editar pesquisa' }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  destinations: state.global.destinations,
});
export default withRouter(
  connect(mapStateToProps, {
    setQuotationParams,
    cleanComparisonPlans,
  })(SearchBarMobile)
);



// WEBPACK FOOTER //
// ./src/views/v2/search/components/searchBarMobile/index.jsx